import React from 'react';
import get from 'lodash/get';
import { hydrate, unmountComponentAtNode } from 'react-dom';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import getBrowserHistory, {
    overrideBrowserHistory,
} from 'yoda-core-components/lib/navigation/history/getBrowserHistory';
import onJcpLogout from 'yoda-site-components/lib/auth/onJcpLogout';

import windowOnError from 'yoda-site-components/lib/analytics/windowOnError';
import ErrorFullPageLazy from './components/ErrorFullPage/ErrorFullPageLazy';
import ContextProvider from './components/ContextProvider';
import StoreFactory from './redux/storeFactory';
import ABTestHelper from './helpers/ABTest';
import {
    install as installServiceWorker,
    uninstall as uninstallServiceWorker,
} from './sw/serviceWorkerMounter';
import installWebView from './webview/webviewInstall';

export default ({ App, rootSaga, reducers, middlewares, restClient, micrositeParams }) => {
    window.__PRELOADED_STATE__.abTestEngagements = ABTestHelper.getABTestData(
        window.testVersion,
        true
    );
    const store = StoreFactory(reducers, window.__PRELOADED_STATE__, rootSaga, middlewares);
    const container = document.getElementById('content');
    const { micrositeName } = micrositeParams;
    const state = store.getState();
    // Sometimes you can override the API host name using custom header X-YODA-API-HOST. In such
    // scenario server would hint that putting the host in to the store. If it was overriden
    // then client also would use the same host so that it is consistent both server side and client side
    if (!state.context.hostname) {
        const port = window.location.port ? `:${window.location.port}` : '';
        state.context.hostname = `${window.location.hostname}${port}`;
    }

    const {
        hostname = '',
        reqId = '',
        apiPrefixInternal = '',
        apiVersion = null,
        featureFlags = {},
        customErrorPageComponent,
        apiHosts,
        staticHosts,
        deviceType,
        isPreview,
        tzOffset,
        staticRoute,
        staticRoutes,
        isFusionEnabled,
        baseName,
        isNative = false,
    } = state.context;

    // Add basename support in case it is not forbidden via feature flag
    if (!get(state, 'context.featureFlags.excludeBaseName', false)) {
        overrideBrowserHistory({
            basename: (baseName || '').replace(window.location.origin, '').slice(0, -1) || '',
        });
    }

    const context = {
        isBrowser: true, // Set browser as default context
        isSecure: window.location.protocol === 'https:',
        hostname,
        apiPrefixInternal,
        apiVersion,
        featureFlags,
        deviceType,
        isPreview,
        tzOffset,
        staticRoute,
        staticRoutes,
        isFusionEnabled,
    };

    const cloudVersion = get(state.context, 'preferences.nativeCloud', '');
    restClient.setContext({
        isBrowser: context.isBrowser,
        isPreview: context.isPreview,
        tzOffset: context.tzOffset,
        isSecure: context.isSecure,
        hostname: context.hostname, // TODO:PR: If we ever think about using different hostname on server side. This line requires design refactoring!!
        apiPrefixInternal: context.apiPrefixInternal,
        apiVersion: context.apiVersion,
        reqId,
        apiHosts,
        staticHosts,
        deviceType,
        isNative,
        cloudVersion,
    });

    /**
     * Installs or uninstalls a service worker based on feature flag
     */
    if (get(state, 'context.featureFlags.enableServiceWorker', false)) {
        installServiceWorker(state.context);
    } else {
        uninstallServiceWorker();
    }

    /**
     * Create jcpWebApp object to interact with web and native app,
     * appSensor will handle listener and emit events between web and native app.
     */
    if (isNative) {
        installWebView(store);
    }

    const render = (errorPage) => {
        hydrate(
            <ContextProvider context={context}>
                <Provider store={store} key="provider">
                    <Router history={getBrowserHistory()}>
                        {errorPage ? (
                            <ErrorFullPageLazy
                                customErrorPageComponent={customErrorPageComponent}
                                templateType={errorPage.status}
                            />
                        ) : (
                            <App />
                        )}
                    </Router>
                </Provider>
            </ContextProvider>,
            container
        );
    };

    render(state.ErrorPage);

    // This is needed in case you have client critical error and you'd like to show ErrorPage immediately
    let currentErrorValue = state.ErrorPage;
    store.subscribe(() => {
        const previousErrorValue = currentErrorValue;
        currentErrorValue = store.getState().ErrorPage;

        if (currentErrorValue !== null && previousErrorValue !== currentErrorValue) {
            unmountComponentAtNode(container);
            render(currentErrorValue);
        }
    });

    featureFlags.disableOnJcpLogout || onJcpLogout(store);
    featureFlags.enableAbsenseOfWindowOnErrorLogs || windowOnError(micrositeName, store);
};
